import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useApi } from "context/ApiProvider";
import { useAuth } from "context/AuthContext";
import { ExhibitionSessionResponse, isExhibitionSessionValid } from "models/ExhibitionSession";
import { ensureDefaultPrintersLoaded } from "services/Printerservice";
import "../Auth/Login.css";
import "./Exhibition.css";

const ExhibitionEntry: React.FC = () => {
  const api = useApi();
  const navigate = useNavigate();
  const {
    isAuthReady,
    isAuthenticated,
    isExhibition,
    startExhibitionSession,
  } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.getElementById("exhibition-email")?.focus();
  }, []);

  if (isAuthReady && isAuthenticated && isExhibition) {
    return <Navigate to="/exhibition/demo" replace />;
  }

  const requestMicrophone = async (): Promise<boolean> => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) return false;
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      return true;
    } catch {
      return false;
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const normalized = email.trim();
    if (!/^\S+@\S+\.\S+$/.test(normalized)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    setError("");
    const microphonePromise = requestMicrophone();
    const response = await api.post("/api/exhibition/session", { email: normalized });
    const microphoneGranted = await microphonePromise;

    if (!response.ok) {
      if (response.status === 429) {
        setError("Too many attempts. Please wait one minute and try again.");
      } else if (response.status === 503) {
        setError(response.body?.message || "The exhibition demo is temporarily unavailable.");
      } else {
        setError(response.body?.message || "Could not start the exhibition demo. Please try again.");
      }
      setLoading(false);
      return;
    }

    const session = response.body as ExhibitionSessionResponse;
    if (!isExhibitionSessionValid(session)) {
      setError("This exhibition user has expired and cannot log in.");
      setLoading(false);
      return;
    }

    await startExhibitionSession(session, microphoneGranted);
    // The login page does this for an ordinary login, and nothing did it here:
    // a visitor arrived with no printer configuration at all, so asking to
    // print was answered with "no default printer is set up" even on a stand
    // that had one. Keeps whatever the machine already had.
    await ensureDefaultPrintersLoaded(api);
    navigate("/exhibition/demo", { replace: true });
  };

  return (
    <main className="login-container light-mode exhibition-entry">
      <section className="login-box exhibition-entry-card" aria-label="T-Soles exhibition demo">
        <div className="login-logo exhibition-logo">
          <img src="/tsole_with_ai.jpg" alt="T-Soles with voice AI" />
        </div>
        <form className="login-form" onSubmit={submit} noValidate>
          <div className="form-group">
            <label htmlFor="exhibition-email">Email address</label>
            <input
              className="login-input"
              id="exhibition-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="visitor@example.com"
              disabled={loading}
            />
          </div>
          {error && <div className="exhibition-error" role="alert">{error}</div>}
          <button className="login-button btnShine" type="submit" disabled={loading}>
            {loading ? "Preparing your demo…" : "Start demo"}
          </button>
        </form>
        <p className="exhibition-privacy">
          Your temporary demo session expires within 24 hours. No password is required.
        </p>
      </section>
    </main>
  );
};

export default ExhibitionEntry;
